import { accountFeeType } from "constants/fee";
import { orderTypeConstants } from "constants/order";

export function getFeeTypeForOrder(orderType: string): string | null {
  switch (orderType) {
    case orderTypeConstants.PATIENT_RESPONSIBILITY:
      return accountFeeType.CTFForPR;
    case orderTypeConstants.BUNDLED:
      return accountFeeType.ClearTransactionFee;
    case orderTypeConstants.GFE:
      return accountFeeType.ClearHealthFeeForGFE;
    default:
      return null;
  }
}

// =============================================================================
// CLEAR PATIENT FEE CALCULATION HELPERS
// =============================================================================
// These helpers calculate the Clear Patient Fee with rounding to nearest $5.
// The fee is calculated as a percentage of the base amount, then rounded UP
// to the nearest $5 increment.
//
// Example: 3% of $1,670 = $50.10 → rounds to $55.00
//
// To revert to old (exact percentage) logic:
// 1. Uncomment the OLD LOGIC sections
// 2. Comment out the NEW LOGIC sections
// =============================================================================

/**
 * Round a dollar amount UP to the nearest $5 increment.
 * Examples: $50.10 → $55, $34.68 → $35, $350.00 → $350
 * @param amount - Amount in dollars
 * @returns Amount rounded up to nearest $5
 */
export const roundUpToNearest5Dollars = (amount: number): number => {
  if (amount <= 0) return 0;
  return Math.ceil(amount / 5) * 5;
};

/**
 * Calculate the Clear Patient Fee amount with rounding.
 * The fee is calculated as a percentage of the base amount, then rounded UP
 * to the nearest $5.
 *
 * @param baseAmount - Base amount in dollars (without fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Fee amount in dollars, rounded up to nearest $5
 */
export const calculateClearPatientFee = (
  baseAmount: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0 || !baseAmount || baseAmount <= 0) {
    return 0;
  }

  // NEW LOGIC: Round up to nearest $5
  const exactFee = baseAmount * (percentage / 100);
  return roundUpToNearest5Dollars(exactFee);

  // OLD LOGIC (exact percentage, no rounding):
  // return baseAmount * (percentage / 100);
};

/**
 * Add Clear Patient Fee to a base amount.
 * Calculates the fee with rounding and adds it to the base amount.
 *
 * @param baseAmount - Base amount in dollars (without fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Total amount in dollars (base + rounded fee)
 */
export const addClearPatientFee = (
  baseAmount: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0) {
    return baseAmount;
  }

  // NEW LOGIC: Add rounded fee
  const fee = calculateClearPatientFee(baseAmount, percentage);
  return baseAmount + fee;

  // OLD LOGIC (exact percentage):
  // return baseAmount * (1 + percentage / 100);
};

/**
 * Remove Clear Patient Fee from a total amount to get the base amount.
 * This reverses the addClearPatientFee calculation, accounting for rounding.
 *
 * Note: Due to rounding, we need to search for the correct base amount
 * rather than simply dividing.
 *
 * @param totalWithFee - Total amount in dollars (includes fee)
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Base amount in dollars (without fee)
 */
export const removeClearPatientFee = (
  totalWithFee: number,
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0 || !totalWithFee || totalWithFee <= 0) {
    return totalWithFee || 0;
  }

  // NEW LOGIC: Search for base amount that produces this total when fee is added
  const p = percentage / 100;

  // Calculate the range of possible base values
  // Fee can add at most $4.99 extra due to rounding
  const maxBase = totalWithFee / (1 + p);
  const minBase = Math.max(0, (totalWithFee - 5) / (1 + p));

  // Try each cent value in the range (from high to low for efficiency)
  for (
    let baseCents = Math.floor(maxBase * 100);
    baseCents >= Math.floor(minBase * 100);
    baseCents -= 1
  ) {
    const base = baseCents / 100;
    const calculatedTotal = addClearPatientFee(base, percentage);
    if (Math.abs(calculatedTotal - totalWithFee) < 0.005) {
      return base;
    }
  }

  // Fallback to approximation (shouldn't reach here if totalWithFee was calculated correctly)
  return Math.round(maxBase * 100) / 100;

  // OLD LOGIC (exact percentage):
  // return totalWithFee / (1 + percentage / 100);
};

/**
 * Get the fee multiplier for adding fee to a base amount.
 * This returns the multiplier that should be used with the base amount.
 *
 * NOTE: This function is DEPRECATED for new rounding logic.
 * Use addClearPatientFee() instead, as the multiplier approach
 * doesn't work correctly with rounding.
 *
 * @deprecated Use addClearPatientFee() instead
 * @param percentage - Fee percentage (e.g., 3 for 3%)
 * @returns Multiplier (e.g., 1.03 for 3%)
 */
export const getClearPatientFeeMultiplier = (
  percentage: number | null | undefined
): number => {
  if (!percentage || percentage <= 0) {
    return 1;
  }
  return 1 + percentage / 100;
};
